import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import { mapCustomFieldsToForm } from "go-app/components/CustomFields";
import CustomFieldsForm from "go-app/components/CustomFields/CustomFieldsForm";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { adminApi } from "../../../../../../../services/Api/Admin/adminApi";
import { PartnerApi, PartnerUserApi } from "../../../../../../../services/Api/Partner/partnerTypes";
import PartnerProvisionsForm from "../../../components/PartnerProvisionsForm";
import PartnerBasicInfoForm from "./PartnerBasicInfoForm";
import PartnerCompanyDetailsForm from "./PartnerCompanyDetailsForm";

interface Props {
	partner?: PartnerApi;
	customFieldsConfig: CustomFieldTemplateApi[];
}

const mapUsers = (users: PartnerUserApi[]) => {
	return users?.map((user) => {
		return {
			name: user.user_email,
			user_id: Number(user.user_id),
		};
	});
};

const PartnerForm = (props: Props) => {
	const form = useForm<PartnerApi>({
		criteriaMode: "all",
		defaultValues: {
			...props.partner,
			codes: props.partner?.codes ? props.partner.codes : [],
			users: mapUsers(props.partner?.users || []),
			custom_fields: mapCustomFieldsToForm(props.customFieldsConfig, props.partner?.custom_fields || []),
		},
	});
	const { formState, handleSubmit, setError, reset } = form;
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);
	const [loading, setLoading] = useState(false);
	const { addFlash, addSuccessFlash } = useFlash();
	const { t } = useTranslation("admin");
	const history = useHistory();

	const onSubmit = handleSubmit(async (data) => {
		if (!validateCustomErrors()) {
			return;
		}

		setLoading(true);
		try {
			const params = { include: "users,organizations,codes,partners,provisions,parent,custom_fields" };
			if (props.partner?.id) {
				data.id = props.partner?.id;
				const res = await adminApi.updateAdminPartner(data.id, data, params);
				reset({
					...res,
					custom_fields: mapCustomFieldsToForm(props.customFieldsConfig, res.custom_fields),
				});
				addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			} else {
				const res = await adminApi.createAdminPartner(data);
				addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
				history.push(`/admin/partners/${res.id}`);
			}
		} catch (e) {
			handleError.form(e, setError, addFlash);
		}
		setLoading(false);
	});

	return (
		<FormDirty key="user-form" loading={loading} noValidate onSubmit={onSubmit} formState={formState}>
			<PartnerBasicInfoForm form={form} partner={props.partner} />
			<PartnerCompanyDetailsForm form={form} />
			<fieldset className="form-group no-gutters">
				<h5>{t("common.word.advanced", { ns: "lib" })}</h5>
				<CustomFieldsForm
					form={form}
					customFieldsConfig={props.customFieldsConfig}
					customFields={props.partner?.custom_fields || []}
					setErrors={setErrors}
				/>
			</fieldset>
			<PartnerProvisionsForm form={form as any} />
		</FormDirty>
	);
};
export default PartnerForm;
